"use client"

import styled from "@emotion/styled"
import { Tab, Tabs } from "@mui/material"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { useIsDesktop } from "ui"
import Container from "ui/Container"
import { mediaQueries } from "ui/theme/mediaQueries"
import {
  delayTime,
  transitionEase,
  transitionTime,
  useInViewConfig,
} from "ui/transitions/utils"
import { extractProperty } from "utils"
import { AccentPalette, getBlockAccentPalette } from "./accentPalette"
import TabItem from "./TabItem"

export const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "$inview",
})<{ $inview: boolean }>`
  opacity: ${(p) => (p.$inview ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inview ? "translateX(0px)" : `translateX(-100vw)`)};
  transition-delay: ${delayTime}s;
`

const TabsContainer = styled.nav``

export const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== "$accent",
})<{ $accent?: AccentPalette }>`
  font-weight: 400;
  font-size: 20px;
  line-height: 140%;
  height: auto;
  &.Mui-selected {
    font-weight: 700;
  }
  ${({ $accent }) =>
    $accent &&
    `
    border-color: transparent;
    background:
      linear-gradient(#fff, #fff) padding-box,
      ${$accent.fill} border-box;
    &.Mui-selected {
      background: ${$accent.fill};
      color: ${$accent.contrastText};
    }
    &:hover,
    &.Mui-selected:hover {
      background: ${$accent.hover};
      color: ${$accent.contrastText};
    }
    &:active,
    &.Mui-selected:active {
      background: ${$accent.active};
    }
  `}
`

const StyledContainer = styled.div`
  width: 100%;
  padding: 32px 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 48px 0;
  }
`

const StyledSection = styled.section`
  padding: 32px 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 0px;
  }
`

const StyledTabsDiv = styled.div<{ $stickyOffset?: number }>`
  position: sticky;
  top: ${(p) => 64 + (p.$stickyOffset || 0)}px;
  z-index: 10;
  background-color: white;
  width: 100%;
  ${mediaQueries.desktopM} {
    top: ${(p) => 80 + (p.$stickyOffset || 0)}px;
  }
`

const StyledTabsInner = styled.div<{ $scrolled: boolean }>`
  width: 100%;
  box-shadow: ${({ $scrolled }) =>
    $scrolled ? "0px 12px 24px 0px #26303d29" : "none"};
  padding-top: 24px;
  padding-bottom: 24px;
`

export type TabItemType = { subTitle: string; content: string; media: string }[]

type Props = {
  data: any
  stickyOffset?: number
}

export default function TabsContent({ data, stickyOffset = 0 }: Props) {
  const accent = getBlockAccentPalette(data)
  const itemsList = extractProperty(data, "itemsList") || []
  const items = itemsList.map((el: any) => {
    const label = extractProperty(el, "tab")
    const content = extractProperty(el, "itemsList") || []
    const contentItems =
      content.map((el: any) => ({
        subTitle: extractProperty(el, "subTitle"),
        content: extractProperty(el, "content"),
        media: extractProperty(el, "media")?.[0]?.url,
      })) || []
    return {
      label,
      contentItems,
    }
  }) as {
    label: string
    contentItems: TabItemType
  }[]
  // Items without a tab name get no tab button, but their content still renders
  const tabs = items
    .map((item, index) => ({ ...item, itemIndex: index }))
    .filter((item) => item.label?.trim())
  const [value, setValue] = useState(0)
  const [scrolled, setScrolled] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const sectionRefs = useRef<(HTMLElement | null)[]>([])

  const { ref, inView } = useInView({ ...useInViewConfig, threshold: 0 })

  // Remove role="tablist" from the flexContainer
  useLayoutEffect(() => {
    const flexContainer = document.querySelector(".MuiTabs-flexContainer")
    if (flexContainer) {
      flexContainer.removeAttribute("role")

      const observer = new MutationObserver(() => {
        flexContainer.removeAttribute("role")
      })

      observer.observe(flexContainer, {
        attributes: true,
        attributeFilter: ["role"],
      })

      return () => observer.disconnect()
    }
  }, [])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)

    // Scroll the window to the selected tab's section, keeping it clear of
    // the fixed header and the sticky tab bar
    const sectionElement = sectionRefs.current[tabs[newValue].itemIndex]
    if (sectionElement) {
      const yOffset = -(180 + stickyOffset)
      const y =
        sectionElement.getBoundingClientRect().top + window.scrollY + yOffset
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }

  const isDesktop = useIsDesktop()

  // Use Intersection Observer instead of scroll events
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the sentinel is NOT visible (scrolled past), tabs should appear scrolled
        setScrolled(!entry.isIntersecting)
      },
      {
        threshold: 0,
        rootMargin: isDesktop ? "-80px 0px 0px 0px" : "-64px 0px 0px 0px",
      },
    )

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current)
    }

    return () => observer.disconnect()
  }, [isDesktop])
  return (
    <>
      {/* Sentinel element to detect when tabs should appear scrolled */}
      <div ref={sentinelRef} style={{ height: "1px", width: "100%" }} />

      {tabs.length > 0 && (
        <StyledTabsDiv ref={containerRef} $stickyOffset={stickyOffset}>
          <StyledTabsInner $scrolled={scrolled}>
            <Container paddingMobile={true}>
              <TabsContainer>
                <StyledTabs
                  $inview={inView}
                  value={value}
                  onChange={handleChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  tabIndex={-1}
                >
                  {tabs.map((tab, index) => (
                    <StyledTab
                      label={tab.label}
                      key={index}
                      aria-level="2"
                      role="presentation"
                      aria-hidden="true"
                      tabIndex={-1}
                      aria-label={`Navigate to ${tab.label} section`}
                      id={`nav-button-${tab.itemIndex}`}
                      $accent={accent}
                    />
                  ))}
                </StyledTabs>
              </TabsContainer>
            </Container>
          </StyledTabsInner>
        </StyledTabsDiv>
      )}
      <Container paddingMobile={true}>
        <StyledContainer ref={ref}>
          {items.map((el, index) => (
            <StyledSection
              id={el.label?.trim() ? el.label : undefined}
              key={index}
              ref={(node) => {
                sectionRefs.current[index] = node
              }}
              aria-labelledby={
                el.label?.trim() ? `nav-button-${index}` : undefined
              }
            >
              <TabItem data={el.contentItems} />
            </StyledSection>
          ))}
          {/* <CommonTabs data={tabsData} value={value} setValue={setValue} /> */}
        </StyledContainer>
      </Container>
    </>
  )
}
